import { useState, useEffect, useRef } from "react";
import { Wand2, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Edit2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const API = "/api";

function FixedItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="bg-card rounded-lg border border-border/60 px-3 py-2" data-testid={`sys-fix-fixed-${item.question_id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-sm font-medium text-left w-full hover:underline" data-testid={`sys-fix-toggle-${item.question_id}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
        <span className="truncate flex-1">{item.title}</span>
        <Badge variant="outline" className="text-[10px]">{item.pool === "daily" ? "Warm Up" : "Challenge"}</Badge>
      </button>
      {open && (
        <div className="mt-2 grid gap-2 sm:grid-cols-2 text-xs font-mono">
          <pre className="bg-red-50/70 dark:bg-red-900/10 rounded px-2 py-1.5 whitespace-pre-wrap overflow-x-auto" data-testid={`sys-fix-before-${item.question_id}`}>{item.before}</pre>
          <pre className="bg-emerald-50/70 dark:bg-emerald-900/10 rounded px-2 py-1.5 whitespace-pre-wrap overflow-x-auto" data-testid={`sys-fix-after-${item.question_id}`}>{item.after}</pre>
        </div>
      )}
    </li>
  );
}

export function SysFixPanel({ disabled, onFixed, onEditQuestion, refreshKey }) {
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [showFixed, setShowFixed] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    const res = await fetch(`${API}/admin/solution-check/sys-fix`, { credentials: "include" });
    if (!res.ok) throw new Error("status");
    return res.json();
  };

  useEffect(() => { if (refreshKey) fetchStatus().then(setJob).catch(() => {}); }, [refreshKey]);

  const poll = async () => {
    try {
      const data = await fetchStatus();
      setJob(data);
      if (data.status === "running") { pollRef.current = setTimeout(poll, 1500); return; }
      setRunning(false);
      if (data.status === "done") {
        onFixed?.();
        if (data.fixed > 0) toast.success(`Fixed ${data.fixed} sample solution${data.fixed === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} still need a manual edit` : ""}`);
        else toast.warning(data.total === 0 ? "No sample solutions use sys" : `None could be fixed automatically - ${data.skipped} need a manual edit`);
      } else if (data.status === "failed") {
        toast.error(data.error || "The sys fix failed");
      }
    } catch {
      setRunning(false);
      toast.error("Could not read the sys fix status");
    }
  };

  useEffect(() => {
    fetchStatus().then((data) => {
      setJob(data);
      if (data.status === "running") { setRunning(true); pollRef.current = setTimeout(poll, 1500); }
    }).catch(() => {});
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/admin/solution-check/sys-fix`, { method: "POST", credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Could not start the sys fix");
        setRunning(false);
        return;
      }
      setJob((prev) => ({ ...prev, status: "running", checked: 0, total: prev?.candidates || 0 }));
      pollRef.current = setTimeout(poll, 1500);
    } catch {
      toast.error("Could not start the sys fix");
      setRunning(false);
    }
  };

  if (!job) return null;
  const candidates = job.candidates || 0;
  const done = job.status === "done" && !running;
  const progressPct = job.total ? Math.round(((job.checked || 0) / job.total) * 100) : 0;

  return (
    <div className="mt-4 rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3" data-testid="sys-fix-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="font-medium flex items-center gap-2"><Wand2 className="w-4 h-4 text-violet-500" /> Fix <code className="text-xs bg-muted px-1 py-0.5 rounded">import sys</code> solutions</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Older generated solutions read input through <code>sys</code>, which the sandbox blocks. This strips <code>import sys</code> (and the <code>sys.stdin</code> / <code>sys.stdout</code> / <code>sys.exit</code> calls), re-runs the tests and only saves solutions that pass.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm ${candidates ? "text-amber-600 dark:text-amber-400" : "text-emerald-600"}`} data-testid="sys-fix-candidates">
            {candidates ? `${candidates} solution${candidates === 1 ? " uses" : "s use"} sys` : "No solutions use sys"}
          </span>
          <Button size="sm" variant="outline" disabled={disabled || running || !candidates} onClick={start} className="gap-1.5 border-violet-400 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-950/40" data-testid="sys-fix-run-btn">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Strip import sys &amp; re-check
          </Button>
        </div>
      </div>

      {running && (
        <div data-testid="sys-fix-progress">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rewriting and re-checking…</span>
            <span>{job.checked || 0} / {job.total || "…"}</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-violet-500 transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      {done && (
        <div className="space-y-2" data-testid="sys-fix-results">
          <p className="text-sm flex items-center gap-2 flex-wrap">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <strong data-testid="sys-fix-fixed-count">{job.fixed}</strong> fixed
            {job.skipped > 0 && <> · <AlertTriangle className="w-4 h-4 text-amber-500" /> <strong data-testid="sys-fix-skipped-count">{job.skipped}</strong> need a manual edit</>}
            {job.finished_at && <span className="text-xs text-muted-foreground">· {new Date(job.finished_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>}
            {job.fixed > 0 && (
              <button type="button" className="text-xs text-violet-600 hover:underline ml-auto" onClick={() => setShowFixed(!showFixed)} data-testid="sys-fix-show-fixed">
                {showFixed ? "Hide" : "Show"} before / after
              </button>
            )}
          </p>
          {showFixed && <ul className="space-y-1.5">{(job.results?.fixed || []).map((item) => <FixedItem key={item.question_id} item={item} />)}</ul>}
          {job.skipped > 0 && (
            <ul className="space-y-1.5" data-testid="sys-fix-skipped-list">
              {(job.results?.skipped || []).map((item) => (
                <li key={item.question_id} className="bg-amber-50/60 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`sys-fix-skipped-${item.question_id}`}>
                  <span className="text-sm font-medium truncate flex-1 min-w-0">{item.title}</span>
                  <span className="text-xs text-amber-700 dark:text-amber-400">{item.reason}</span>
                  {onEditQuestion && (
                    <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" onClick={() => onEditQuestion(item.question_id)} data-testid={`sys-fix-edit-${item.question_id}`}>
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
